import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Button from './Button/Button';
import { useAuth } from '../context/AuthContext';
import Avatar from './Avatar/Avatar';
import styles from './TeamMembers.module.scss';
import { getAvatarSrc } from '../utils/avatarUtils';
import Input from './Input/Input';
import Select from './Select/Select';
import Modal from './Modal';

function TeamMembers({ reloadAll }) {
  const { user: currentUser } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [combinedMembers, setCombinedMembers] = useState([]);
  const [editingMember, setEditingMember] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [updatingRole, setUpdatingRole] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(''); // 'team-member' or 'user-role'
  const [selectedMember, setSelectedMember] = useState(null);
  
  const isCurrentUserAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    loadData();
    loadDepartments();
  }, []);

  // Load departments
  const loadDepartments = async () => {
    try {
      const res = await api.getDepartments();
      setDepartments(res.data);
      if (res.data.length > 0) {
        setSelectedDepartmentId(res.data[0].id);
      }
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  // Load both team members and users
  const loadData = async () => {
    try {
      const [teamMembersRes, usersRes] = await Promise.all([
        api.getTeamMembers(selectedDepartmentId ? { department_id: selectedDepartmentId } : {}),
        api.getUsers()
      ]);
      
      setTeamMembers(teamMembersRes.data);
      setUsers(usersRes.data);
      
      // Process and combine the data
      combineData(teamMembersRes.data, usersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  // Update data when department changes
  useEffect(() => {
    if (selectedDepartmentId) {
      loadData();
    }
  }, [selectedDepartmentId]);

  // Combine users and team members based on email
  const combineData = (teamMembers, users) => {
    const combined = [];
    const processedEmails = new Set();
    
    // Add team members with matching users
    teamMembers.forEach(member => {
      const matchingUser = member.email ? users.find(user => user.email === member.email) : null;
      combined.push({
        id: member.id,
        name: member.name,
        email: member.email,
        picture_url: member.picture_url,
        department_id: member.department_id,
        isTeamMember: true,
        isUser: !!matchingUser,
        userId: matchingUser?.id,
        userRole: matchingUser?.role,
        // Use team member data but keep track of user info
        source: {
          teamMember: member,
          user: matchingUser
        }
      });
      
      if (member.email) {
        processedEmails.add(member.email);
      }
    });
    
    // Add users without a team member entry
    users.forEach(user => {
      if (user.email && !processedEmails.has(user.email)) {
        combined.push({
          id: `user-${user.id}`,
          name: user.name,
          email: user.email,
          picture_url: user.picture_url,
          isTeamMember: false,
          isUser: true,
          userId: user.id,
          userRole: user.role,
          source: {
            user: user,
            teamMember: null
          }
        });
      }
    });
    
    setCombinedMembers(combined);
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    try {
      // Find the member being edited to check if they're also a user
      const memberToUpdate = combinedMembers.find(m => 
        m.isTeamMember && m.id === editingMember.id
      );
      
      let updateData = { ...editingMember };
      
      // If this is a team member who is also a user, only update the name
      if (memberToUpdate && memberToUpdate.isUser) {
        updateData = {
          id: editingMember.id,
          name: editingMember.name,
          email: memberToUpdate.email,
          picture_url: memberToUpdate.picture_url,
          department_id: editingMember.department_id
        };
      }
      
      await api.updateTeamMember(updateData.id, updateData);
      closeModal();
      loadData();
      reloadAll();
    } catch (error) {
      console.error('Error updating team member:', error);
    }
  };

  const handleDeleteMember = async (id) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        await api.deleteTeamMember(id);
        loadData();
        reloadAll();
      } catch (error) {
        console.error('Error deleting team member:', error);
      }
    }
  };

  const handleCreateTeamMemberFromUser = async (user) => {
    try {
      // Create team member from user data
      await api.createTeamMember({
        name: user.name,
        email: user.email,
        picture_url: user.picture_url,
        department_id: selectedDepartmentId
      });
      
      // Wait for the operation to complete and reload data
      await loadData();
      
      // Also reload parent component data if needed
      if (reloadAll) {
        reloadAll();
      }
    } catch (error) {
      console.error('Error creating team member from user:', error);
    }
  };

  // Handle role change for a user
  const handleRoleChange = async (userId, newRole) => {
    if (!isCurrentUserAdmin) return;
    
    // Prevent changing your own role to avoid locking yourself out
    const isCurrentUser = userId === currentUser.id;
    if (isCurrentUser && newRole !== 'admin') {
      alert("You cannot remove admin rights from your own account.");
      return;
    }

    try {
      setUpdatingRole(true);
      await api.updateUserRole(userId, newRole);
      
      // Update local state to reflect the change
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        )
      );
      
      // Refresh combined data
      await loadData();
      closeModal();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role. Please try again.');
    } finally {
      setUpdatingRole(false);
    }
  };

  // Open modal to edit a team member
  const openEditTeamMemberModal = (member) => {
    setEditMode('team-member');
    setEditingMember(member.source.teamMember);
    setSelectedMember(member);
    setModalOpen(true);
  };

  // Open modal to edit a user's role
  const openEditUserRoleModal = (member) => {
    setEditMode('user-role');
    setSelectedMember(member);
    setModalOpen(true);
  };

  // Close modal and reset state
  const closeModal = () => {
    setModalOpen(false);
    setEditingMember(null);
    setSelectedMember(null);
    setEditMode('');
  };

  // Transform departments for Select component
  const departmentOptions = departments.map(dept => ({
    value: dept.id,
    label: dept.name
  }));

  // Role options for the dropdown
  const roleOptions = [
    { value: 'user', label: 'User' },
    { value: 'admin', label: 'Admin' }
  ];

  const renderMemberCard = (member) => {
    // Regular card display
    return (
      <div key={member.id} className={`card ${styles.memberCard}`}>
        <div className={styles.memberCardInner}>
          <Avatar
            src={getAvatarSrc(member, currentUser)}
            name={member.name}
            size="xlarge"
            className={styles.memberAvatar}
          />
          <h3>{member.name}</h3>
          {member.email && <p className="email">{member.email}</p>}
          <div className={styles.memberBadges}>
            {member.isUser && <span className={styles.adminBadge}>{member.userRole.charAt(0).toUpperCase() + member.userRole.slice(1)}</span>}           
            {member.department_id && (
              <span className={styles.departmentBadge}>
                {departments.find(d => d.id === member.department_id)?.name || 'Unknown Department'}
              </span>
            )}
          </div>
        </div>

        <div className="card__actions">
          {member.isUser && !member.isTeamMember && (
            <>
              {isCurrentUserAdmin && (
                <Button 
                  type="secondary" 
                  onClick={() => openEditUserRoleModal(member)}
                >
                  Edit
                </Button>
              )}
                            <Button 
                type="secondary" 
                onClick={() => handleCreateTeamMemberFromUser(member.source.user)}
              >
                Add as Team Member
              </Button>
            </>
          )}
          
          {member.isTeamMember && member.isUser && (
            <>
              <Button 
                type="secondary" 
                onClick={() => openEditTeamMemberModal(member)}
              >
                Edit
              </Button>
              <Button 
                type="destructive" 
                onClick={() => handleDeleteMember(member.source.teamMember.id)}
              >
                Remove Team Member
              </Button>
            </>
          )}
          
          {member.isTeamMember && !member.isUser && (
            <>
              <Button 
                type="secondary" 
                onClick={() => openEditTeamMemberModal(member)}
              >
                Edit
              </Button>
              <Button type="destructive" onClick={() => handleDeleteMember(member.id)}>
                Delete
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  // Render the edit team member modal content
  const renderTeamMemberModalContent = () => {
    if (!selectedMember || !editingMember) return null;
    
    return (
      <form onSubmit={handleUpdateMember}>
        <div className="form-group">
          <Input
            label="Name"
            type="text"
            value={editingMember.name}
            onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
            required
            id="edit-member-name"
          />
        </div>
        {!selectedMember.isUser && (
          <>
            <div className="form-group">
              <Input
                label="Email"
                type="email"
                placeholder="Email address"
                value={editingMember.email || ''}
                onChange={(e) => setEditingMember({ ...editingMember, email: e.target.value })}
                id="edit-member-email"
              />
            </div>
            <div className="form-group">
              <Input
                label="Profile Picture URL"
                type="text"
                placeholder="Picture URL"
                value={editingMember.picture_url || ''}
                onChange={(e) => setEditingMember({ ...editingMember, picture_url: e.target.value })}
                id="edit-member-picture"
              />
            </div>
          </>
        )}
        <div className="form-group">
          <Select
            label="Department"
            options={departmentOptions}
            value={editingMember.department_id || ''}
            onChange={(value) => setEditingMember({ ...editingMember, department_id: value })}
            placeholder="Select a department"
            id="edit-member-department"
            required
            showSearch={false}
          />
        </div>
        
        {/* Role selector in edit mode - only for users, only for admins */}
        {isCurrentUserAdmin && selectedMember.isUser && (
          <div className={`form-group ${styles.roleSelector}`}>
            <Select
              label="User Role"
              options={roleOptions}
              value={selectedMember.userRole}
              onChange={(value) => handleRoleChange(selectedMember.userId, value)}
              id={`role-select-${selectedMember.userId}`}
              disabled={updatingRole || selectedMember.userId === currentUser.id}
              showSearch={false}
            />
            {selectedMember.userId === currentUser.id && (
              <p className={styles.selfRoleNote}>Note: You cannot change your own role.</p>
            )}
          </div>
        )}
        
        {selectedMember.isUser && (
          <p className={styles.editNote}>Note: Only the team member name and department can be edited. Email and picture are synced with the user account.</p>
        )}
      </form>
    );
  };

  // Render the user role modal content
  const renderUserRoleModalContent = () => {
    if (!selectedMember) return null;
    
    return (
      <div>
        <div className={styles.modalUserInfo}>
          <Avatar
            src={getAvatarSrc(selectedMember, currentUser)}
            name={selectedMember.name}
            size="large"
          />
          <div>
            <h3>{selectedMember.name}</h3>
            <p>{selectedMember.email}</p>
            <div className={styles.memberBadges}>
              <span className={styles.adminBadge}>{selectedMember.userRole}</span>
              <span className={styles.userBadge}>User</span>
            </div>
          </div>
        </div>
        
        <div className={`form-group ${styles.roleSelector}`}>
          <Select
            label="User Role"
            options={roleOptions}
            value={selectedMember.userRole}
            onChange={(value) => handleRoleChange(selectedMember.userId, value)}
            id={`modal-role-select-${selectedMember.userId}`}
            disabled={updatingRole || selectedMember.userId === currentUser.id}
            showSearch={false}
          />
          {selectedMember.userId === currentUser.id && (
            <p className={styles.selfRoleNote}>Note: You cannot change your own role.</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="form-group mb-4">
        <label>Filter by Department</label>
        <Select
          options={departmentOptions}
          showSearch={false}
          value={selectedDepartmentId}
          onChange={(value) => setSelectedDepartmentId(value)}
          placeholder="Select a department"
          id="department-filter"
        />
      </div>

      <h2 className={styles.sectionTitle}>Team Members & Users</h2>
      <div className="grid">
        {combinedMembers.map(renderMemberCard)}
      </div>

      {/* Edit Team Member Modal */}
      <Modal
        isOpen={modalOpen && editMode === 'team-member'}
        onClose={closeModal}
        title="Edit Team Member"
        footer={
          <Modal.Actions
            onCancel={closeModal}
            onConfirm={handleUpdateMember}
            cancelText="Cancel"
            confirmText="Save Changes"
          />
        }
      >
        {renderTeamMemberModalContent()}
      </Modal>

      {/* Edit User Role Modal */}
      <Modal
        isOpen={modalOpen && editMode === 'user-role'}
        onClose={closeModal}
        title="Edit User Role"
        size="small"
        footer={
          <Modal.Actions
            onCancel={closeModal}
            cancelText="Close"
          />
        }
      >
        {renderUserRoleModalContent()}
      </Modal>
    </div>
  );
}

export default TeamMembers; 